import { useCallback } from 'react';
import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router';
import { HeaderDashboard } from '@/components/shared/header-dashboard';
import { BookingListCard } from '@/features/cleaner/booking-list-card';
import { cn } from '@/lib/utils';

type BookingsTab = 'upcoming' | 'history' | 'all';

export const Route = createFileRoute(
  '/(cleaner)/_authenticated/cleaner/bookings'
)({
  component: BookingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: ['upcoming', 'history', 'all'].includes(search.status as string)
      ? (search.status as BookingsTab)
      : 'upcoming',
  }),
});

function BookingsPage() {
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { status: urlStatus } = Route.useSearch();
  const tab: BookingsTab =
    urlStatus && ['upcoming', 'history', 'all'].includes(urlStatus)
      ? urlStatus
      : 'upcoming';
  const isDetailPage = matchRoute({
    to: '/cleaner/bookings/$id',
  });

  const handleTabChange = useCallback(
    (newTab: BookingsTab) => {
      navigate({
        to: '/cleaner/bookings',
        search: { status: newTab },
        replace: true,
      });
    },
    [navigate]
  );

  return (
    <div>
      {!isDetailPage && (
        <>
          <HeaderDashboard>
            <HeaderDashboard.Title>Bookings</HeaderDashboard.Title>
            <HeaderDashboard.Action>
              <button
                type="button"
                onClick={() => handleTabChange('all')}
                className={cn(
                  'text-sm font-medium transition-colors mr-6 cursor-pointer',
                  tab === 'all'
                    ? 'text-[#FF783A]'
                    : 'text-gray-900 dark:text-gray-300 hover:text-[#FF783A]'
                )}
              >
                All Jobs
              </button>

              <button
                type="button"
                onClick={() => handleTabChange('upcoming')}
                className={cn(
                  'text-sm font-medium transition-colors mr-6 cursor-pointer',
                  tab === 'upcoming'
                    ? 'text-[#FF783A]'
                    : 'text-gray-900 dark:text-gray-300 hover:text-[#FF783A]'
                )}
              >
                Upcoming Jobs
              </button>

              <button
                type="button"
                onClick={() => handleTabChange('history')}
                className={cn(
                  'text-sm font-medium transition-colors cursor-pointer',
                  tab === 'history'
                    ? 'text-[#FF783A]'
                    : 'text-gray-900 dark:text-gray-300 hover:text-[#FF783A]'
                )}
              >
                History
              </button>
            </HeaderDashboard.Action>
          </HeaderDashboard>
          <div className="border-b border-gray-200 dark:border-gray-700 w-full mb-6" />
        </>
      )}
      <Outlet />
      {!isDetailPage && <BookingListCard activeTab={tab} />}
    </div>
  );
}
